import React, {useEffect, useState} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  StyleSheet
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import OrderItem from '../../commons/orderItem';
import OrderItemImage from '../../commons/orderItemImage';
import OrderKitItem from '../../commons/orderKitItem';
import {redirectToProductDetail, selectFocusedOrder} from '../../actions/ScanToCartScanningActions';
import { filterItemsByOrderId, filterItemsByOrderItemIds, getUnscannedPieceCount } from './utils/scanningUtils';

const UnScannedItemsList = ({onShuffleItem = () => {}}) => {
  const dispatch = useDispatch();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const focusedOrderId = useSelector(selectFocusedOrder)?.id ?? null;
  const allUnScannedItems = useSelector(
    state => state.scanToCart.unScannedItems
  );
  const allNotFullFilledItems = useSelector(
    state => state.scanToCart.notFullFilledItems
  );
  const unScannedItems = filterItemsByOrderId(allUnScannedItems, focusedOrderId);
  const notFullFilledItems = filterItemsByOrderId(allNotFullFilledItems, focusedOrderId);

  useEffect(() => {
    setItemsPerPage(10);
  }, [focusedOrderId]);
  const isPicked = useSelector(
    state => state.scanToCart.activeCart.isPicked
  );
  const isScanComplete = useSelector(
    state => state.scanToCartScanning.cartScanComplete
  );
  const nextPickItem =  useSelector(state => state.scanToCart.nextPickItem)
  const totalUnScannedCount = getUnscannedPieceCount(unScannedItems);

  const totalNotFulfilledCount = notFullFilledItems.reduce((count, item) => {
    if (item.product_type === 'individual' && item.child_items?.length > 0) {
      return (
        count +
        item.child_items.reduce(
          (childCount, child) => childCount + (child.skipped_qty || 0),
          0
        )
      );
    }
    return count + (item.skipped_qty || 0);
  }, 0);

  const onItemPress = item => {
    dispatch(redirectToProductDetail(item.product_id));
  };

  const renderItem = (item, index) => {
    const isKit =
      item.product_type === 'individual' && item.child_items?.length > 0;

    if (isKit) {
      return (
        <View
          key={`kit-${item.product_id}-${index}`}
          style={styles.kitItemWrapper}>
          <OrderKitItem
            item={item}
            index={index}
            listType={'unscanned'}
            redirectToItemDetail={onItemPress}
            shuffleTheItem={onShuffleItem}
            shuffleTheKitItem={(kitSku, itemSku) =>
              onShuffleItem(kitSku, itemSku)
            }
          />
        </View>
      );
    } else {
      // Single item or depends type
      return (
        <View
          key={`single-${item.product_id}-${index}`}
          style={styles.singleItemWrapper}>
          <View style={styles.singleItemContainer}>
            <OrderItem
              item={item}
              redirectToItemDetail={onItemPress}
              testID={'redirectToItem'}
            />
            <OrderItemImage
              item={item}
              shuffleTheItem={onShuffleItem}
              testID={'shuffleItem'}
              listType={'unscanned'}
            />
          </View>
        </View>
      );
    }
  };

  const renderNotFulfilledItem = (item, index) => {
    return (
      <View
        key={`not-fulfilled-single-${item.product_id}-${index}`}
        style={styles.singleItemWrapper}>
        <View style={styles.singleItemContainer}>
          <OrderItem
            item={item}
            redirectToItemDetail={onItemPress}
            testID={'redirectToItem'}
          />
          <OrderItemImage
            item={item}
            shuffleTheItem={onShuffleItem}
            testID={'shuffleItem'}
            listType={'notFulfilled'}
            isNotFullFilled={true}
          />
        </View>
      </View>
    );
  };

  const displayedItems = unScannedItems.slice(0, itemsPerPage);
  const hasMoreItems = unScannedItems.length > itemsPerPage;

  return (
    <View style={styles.container}>
      <Text style={styles.headerTitle}>
        {totalUnScannedCount} Unscanned Items
      </Text>

      <ScrollView
        style={styles.scrollContainer}
        showsVerticalScrollIndicator={false}>
        {displayedItems.length > 0 ? (
          displayedItems.map((item, index) => renderItem(item, index))
        ) : (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {focusedOrderId === null ? 'No unscanned items' : 'No unscanned items for this order'}
            </Text>
          </View>
        )}

        {/* Show More Button */}
        {hasMoreItems && (
          <TouchableOpacity
            style={styles.showMoreButton}
            onPress={() => setItemsPerPage(prev => prev + 10)}>
            <Text style={styles.showMoreText}>Show More</Text>
          </TouchableOpacity>
        )}

        {/* Reset Button */}
        {itemsPerPage > 10 && (
          <TouchableOpacity
            style={styles.resetButton}
            onPress={() => setItemsPerPage(10)}>
            <Text style={styles.resetText}>Reset</Text>
          </TouchableOpacity>
        )}

        {/* Not Fulfilled Items Section */}
        {notFullFilledItems.length > 0 && (
          <View style={styles.notFulfilledSection}>
            <Text style={styles.notFulfilledHeaderTitle}>
              {totalNotFulfilledCount} Not Fulfilled Items
            </Text>
            {notFullFilledItems.map((item, index) => renderNotFulfilledItem(item, index))}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent'
  },
  headerTitle: {
    color: '#fff',
    fontSize: 15,
    fontFamily: 'Poppins_600SemiBold',
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#000',
    paddingVertical: 10
  },
  scrollContainer: {
    flex: 1
  },

  singleItemWrapper: {
    marginBottom: 8
  },
  singleItemContainer: {
    flexDirection: 'row',
    borderRadius: 30
  },
  kitItemWrapper: {
    marginBottom: 4
  },

  // Action Buttons
  showMoreButton: {
    backgroundColor: '#41970f',
    padding: 12,
    borderRadius: 5,
    margin: 10,
    alignItems: 'center'
  },
  showMoreText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold'
  },
  resetButton: {
    backgroundColor: '#f54400',
    padding: 12,
    borderRadius: 5,
    margin: 10,
    alignItems: 'center'
  },
  resetText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold'
  },

  // Empty State
  emptyContainer: {
    padding: 20,
    alignItems: 'center'
  },
  emptyText: {
    color: '#fff',
    fontSize: 16
  },

  // Not Fulfilled Items Section
  notFulfilledSection: {
    marginTop: 20,
    paddingTop: 10
  },
  notFulfilledHeaderTitle: {
    color: '#fff',
    fontSize: 15,
    fontFamily: 'Poppins_600SemiBold',
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#090909ff',
    paddingVertical: 10,
  }
});

export default UnScannedItemsList;
